import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import HomeTab from "./tabs/HomeTab";
import MyPageTab from "./tabs/MyPageTab";
import NowTab from "./tabs/NowTab";
import ActivityTab from "./tabs/ActivityTab";
import CalendarTab from "./tabs/CalendarTab";
import { api, setAdminView } from "@/lib/api";

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminView = searchParams.get("admin_view") || "";
  const [tab, setTab] = useState("home");
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setAdminView(adminView);
    return () => setAdminView(null);
  }, [adminView]);

  const reload = async () => {
    const [{ business }, { services }] = await Promise.all([
      api("/business/me"),
      api("/services"),
    ]);
    if (!business) {
      navigate("/onboarding", { replace: true });
      return;
    }
    setBusiness(business);
    setServices(services);
    setLoaded(true);
  };

  useEffect(() => {
    setLoaded(false);
    setError("");
    reload().catch((e) => {
      if (adminView) setError(e.message || "Could not open this business.");
      else navigate("/login", { replace: true });
    });
  }, [adminView]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F7F7F5] px-5 text-center font-sans" data-testid="admin-view-error">
        <ShieldAlert size={28} className="text-amber-600" />
        <h1 className="mt-4 font-serif text-2xl font-medium text-guinep-ink">Cannot open this dashboard</h1>
        <p className="mt-2 max-w-xs text-sm text-guinep-mute">{error}</p>
        <Link to="/admin" data-testid="admin-view-error-back" className="mt-6 rounded-full bg-guinep-deep px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid">
          Return to Admin
        </Link>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-guinep-line border-t-guinep-deep" />
      </div>
    );
  }

  const readOnly = !!adminView;

  return (
    <AppShell tab={tab} setTab={setTab} adminView={readOnly} businessName={business.name}>
      {tab === "home" && <HomeTab user={user} business={business} services={services} reload={reload} goCalendar={() => setTab("calendar")} readOnly={readOnly} />}
      {tab === "calendar" && <CalendarTab business={business} reload={reload} readOnly={readOnly} />}
      {tab === "mypage" && <MyPageTab business={business} services={services} reload={reload} readOnly={readOnly} />}
      {tab === "now" && <NowTab reload={reload} readOnly={readOnly} />}
      {tab === "activity" && <ActivityTab goCalendar={() => setTab("calendar")} />}
    </AppShell>
  );
};

export default Dashboard;
